import { Link, useNavigate, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getComicById } from '@/utils/comicHelpers';
import { useSeo } from '@/hooks/useSeo';

export default function ReadPage() {
  const { id, num } = useParams();
  const navigate = useNavigate();
  const comic = getComicById(id);
  const chapterNum = Number(num);
  const chapter = comic?.chapters.find((c) => c.num === chapterNum);

  useSeo({
    title: comic && chapter
      ? `《${comic.title}》第${chapter.num}话 ${chapter.title}-在线免费阅读-布丁漫画网`
      : '章节不存在-布丁漫画网',
    description: comic && chapter
      ? `《${comic.title}》漫画第${chapter.num}话「${chapter.title}」在线免费阅读，高清流畅浏览，全集中文版本尽在布丁漫画网。`
      : '您访问的章节不存在，请返回布丁漫画网继续阅读。',
    keywords: comic ? `${comic.title},第${chapterNum}话,漫画,在线免费阅读,布丁漫画网` : '布丁漫画网,漫画,在线免费阅读',
    path: `/read/${id ?? ''}/${num ?? ''}`,
    jsonLd:
      comic && chapter
        ? [
            {
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: `${window.location.origin}${__BASE_PATH__ || ''}/`,
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: comic.title,
                  item: `${window.location.origin}${__BASE_PATH__ || ''}/comic/${comic.id}`,
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: `第${chapter.num}话`,
                  item: `${window.location.origin}${__BASE_PATH__ || ''}/read/${comic.id}/${chapter.num}`,
                },
              ],
            },
          ]
        : [],
  });

  if (!comic || !chapter) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-24 text-center md:px-6">
        <i className="ri-book-2-line text-5xl text-foreground-300"></i>
        <h1 className="mt-4 text-2xl text-foreground-950">章节不存在</h1>
        <p className="mt-2 text-sm text-foreground-600">
          您访问的章节不存在或已被移除，请返回作品页选择其他章节。
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link
            to={`/comic/${id}`}
            className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            返回作品页
          </Link>
          <Link
            to="/category"
            className="whitespace-nowrap rounded-md border border-primary-200 bg-background-50 px-6 py-2.5 text-sm text-primary-600 transition-colors hover:bg-primary-500 hover:text-background-50"
          >
            浏览全部漫画
          </Link>
        </div>
      </div>
    );
  }

  const hasPrev = chapterNum > 1;
  const hasNext = chapterNum < comic.chapters.length;
  const basePath = `/read/${comic.id}`;

  return (
    <div className="bg-background-100">
      <div className="sticky top-16 z-40 border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-3 px-4 md:px-6">
          <Link
            to={`/comic/${comic.id}`}
            className="flex shrink-0 items-center gap-1 whitespace-nowrap text-sm text-foreground-700 transition-colors hover:text-primary-600"
          >
            <i className="ri-arrow-left-line"></i>
            <span className="hidden sm:inline">返回目录</span>
          </Link>

          <div className="min-w-0 flex-1 text-center">
            <p className="truncate text-sm font-medium text-foreground-900">
              《{comic.title}》第{chapter.num}话 {chapter.title}
            </p>
          </div>

          <select
            value={chapterNum}
            onChange={(e) => navigate(`${basePath}/${Number(e.target.value)}`)}
            aria-label="选择章节"
            className="hidden h-9 w-44 rounded-md border border-background-200 bg-background-100 px-2 text-sm text-foreground-800 outline-none md:block"
          >
            {comic.chapters.map((c) => (
              <option key={c.id} value={c.num}>
                第{c.num}话 {c.title}
              </option>
            ))}
          </select>

          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={() => navigate(`${basePath}/${chapterNum - 1}`)}
              disabled={!hasPrev}
              className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-lg text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-background-100 disabled:hover:text-foreground-700"
              aria-label="上一话"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            <button
              type="button"
              onClick={() => navigate(`${basePath}/${chapterNum + 1}`)}
              disabled={!hasNext}
              className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-lg text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-background-100 disabled:hover:text-foreground-700"
              aria-label="下一话"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[760px] px-4 py-8 md:px-6 md:py-10">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: comic.title, to: `/comic/${comic.id}` },
            { label: `第${chapter.num}话` },
          ]}
        />

        <h1 className="mt-4 text-xl text-foreground-950 md:text-2xl">
          《{comic.title}》第{chapter.num}话 {chapter.title}
        </h1>
        <p className="mt-1 text-xs text-foreground-500">
          {comic.status === '连载中' ? '更新于' : '完结于'} {comic.updateTime}
          ，全站在线免费阅读
        </p>

        <select
          value={chapterNum}
          onChange={(e) => navigate(`${basePath}/${Number(e.target.value)}`)}
          aria-label="选择章节"
          className="mt-5 block h-10 w-full rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-800 outline-none md:hidden"
        >
          {comic.chapters.map((c) => (
            <option key={c.id} value={c.num}>
              第{c.num}话 {c.title}
            </option>
          ))}
        </select>

        <div className="mt-6 flex flex-col items-center gap-6">
          {chapter.pages.map((src, index) => (
            <figure key={`${src}-${index}`} className="w-full">
              <img
                src={src}
                alt={`《${comic.title}》第${chapter.num}话 第${index + 1}页`}
                title={`《${comic.title}》第${chapter.num}话 第${index + 1}页`}
                className="w-full rounded-lg"
              />
              <figcaption className="mt-2 text-center text-xs text-foreground-500">
                第{chapter.num}话 · 第{index + 1}页
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => navigate(`${basePath}/${chapterNum - 1}`)}
            disabled={!hasPrev}
            className="flex items-center gap-1 whitespace-nowrap rounded-md bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-background-50 disabled:hover:text-foreground-700"
          >
            <i className="ri-arrow-left-s-line"></i>
            上一话
          </button>
          <Link
            to={`/comic/${comic.id}`}
            className="flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-list-check-2"></i>
            查看目录
          </Link>
          <button
            type="button"
            onClick={() => navigate(`${basePath}/${chapterNum + 1}`)}
            disabled={!hasNext}
            className="flex items-center gap-1 whitespace-nowrap rounded-md bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-background-50 disabled:hover:text-foreground-700"
          >
            下一话
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    </div>
  );
}